import React, { useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  FaCrosshairs,
  FaCommentDots,
  FaFilm,
  FaRegQuestionCircle,
  FaCheck,
} from "react-icons/fa";

const BIND = 'bind "f3" "say !allstar"';

const FAQ = [
  {
    q: "Does it really not use my clip limit?",
    a: "Right. !allstar clips don't draw from your normal clip allowance. If you're tapped out for the month, you can still keep making them in game.",
  },
  {
    q: "Do I need the Allstar app running?",
    a: "No. The command lives in match chat and the clip is built later from the match demo, so nothing has to be open while you play.",
  },
  {
    q: "What if I type it a little late?",
    a: "You're covered. Sent during the next round's buy time or roughly its first 20 seconds, it looks back and clips the round you just finished.",
  },
  {
    q: "Does team chat work?",
    a: "No, say it in all-chat. Team chat isn't recorded into the match demo, so a team-only !allstar won't be picked up.",
  },
  {
    q: "How do I change the bind key?",
    a: 'Swap "f3" for any key you like, then paste the line into the CS2 console or your autoexec.',
  },
  {
    q: "Why isn't my clip showing up?",
    a: "First, refresh your match history so we can pick up the latest game. If it still isn't there, upload the demo and we'll clip it for you.",
  },
];

const AllstarPage = () => {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(0);
  const [processing, setProcessing] = useState(false);
  const videoRef = useRef(null);

  // Video ends on the !allstar being typed: show a "processing the clip"
  // beat for ~2.6s, then loop back to the start.
  const handleVideoEnded = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      videoRef.current?.play();
    }, 3800);
  };

  const copyBind = async () => {
    try {
      await navigator.clipboard.writeText(BIND);
    } catch (e) {
      /* clipboard unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Page>
      <Hero>
        <div>
          <H1>
            Skip clip limits with <Cmd>!allstar</Cmd>.
          </H1>
          <Lede>
            Just type <Cmd>!allstar</Cmd> in all chat after you do something you
            want to clip. If we can access your match, we&apos;ll make the clip{" "}
            <Strong>for free, even if you&apos;ve hit your clip limit.</Strong>
          </Lede>

          <BindHeader>
            <BindLabel>Bind it to a key so one tap clips your round:</BindLabel>
            <BindHelp tabIndex={0}>
              <FaRegQuestionCircle aria-hidden="true" />
              Where do I put this?
              <BindTooltip role="tooltip">
                <ol>
                  <li>
                    Copy the command above and open the CS2 developer console
                    (the <Cmd>~</Cmd> key). If nothing happens, turn the console
                    on in Game Settings &gt; Game &gt; Enable Developer Console.
                  </li>
                  <li>
                    Want a different key? Swap <Cmd>f3</Cmd> for whatever you
                    like.
                  </li>
                  <li>Paste it into the console and hit Enter.</li>
                  <li>
                    In a match, tap that key after a play to fire off{" "}
                    <Cmd>!allstar</Cmd>.
                  </li>
                </ol>
              </BindTooltip>
            </BindHelp>
          </BindHeader>
          <BindRow>
            <BindInput
              type="text"
              readOnly
              value={BIND}
              aria-label="CS2 bind command"
            />
            <CopyButton type="button" copied={copied} onClick={copyBind}>
              {copied ? "Copied" : "Copy"}
            </CopyButton>
          </BindRow>
          <SubHint>
            No bind? Just type <Cmd>!allstar</Cmd> in chat. It works the same.
          </SubHint>
        </div>

        <div>
          <MediaFrame>
            <Media>
              <video
                ref={videoRef}
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/in-game/example_allstar.mp4`}
                autoPlay
                muted
                playsInline
                onEnded={handleVideoEnded}
              />
              {processing && (
                <Processing>
                  <Spinner aria-hidden="true" />
                  <ProcessingText>
                    <small>
                      <Cmd>!allstar</Cmd> detected
                    </small>
                    Processing <span>Deagle 3K on Dust 2</span>
                  </ProcessingText>
                  <ProcessingBar />
                </Processing>
              )}
            </Media>
          </MediaFrame>
          <MediaCap>
            Someone typing <Cmd>!allstar</Cmd> after a sick 3K!
          </MediaCap>
        </div>
      </Hero>

      <Section>
        <SecTitle>How it works</SecTitle>
        <Steps>
          <Step>
            <div className="head">
              <span className="ico">
                <FaCrosshairs />
              </span>
              <span className="num">01</span>
            </div>
            <h3>Make a play</h3>
            <p>
              Pop off in Premier, Competitive, or Wingman. Works on FACEIT and
              other third-party services too. Nothing needs to be open or
              recording.
            </p>
          </Step>
          <Step>
            <div className="head">
              <span className="ico">
                <FaCommentDots />
              </span>
              <span className="num">02</span>
            </div>
            <h3>Type it in chat</h3>
            <p>
              Send <Cmd>!allstar</Cmd> in all-chat{" "}
              <strong>after you&apos;ve made a play</strong> or at the end of a
              round.
            </p>
          </Step>
          <Step>
            <div className="head">
              <span className="ico">
                <FaFilm />
              </span>
              <span className="num">03</span>
            </div>
            <h3>Captured automatically</h3>
            <p>
              Once your match is processed, we auto-clip every round you called{" "}
              <Cmd>!allstar</Cmd>, and none of them count against your clip
              limit.
            </p>
          </Step>
        </Steps>
      </Section>

      <Section>
        <SecTitle>
          Free vs Premium <Cmd>!allstar</Cmd>
        </SecTitle>
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>Free &amp; Basic</th>
                <th className="paid paidcol">Standard &amp; Unlimited</th>
              </tr>
            </thead>
            <tbody>
              <tr className="hl">
                <th>Counts against clip limit</th>
                <td>
                  <span className="yes">
                    <FaCheck /> Never
                  </span>
                </td>
                <td className="paidcol">
                  <span className="yes">
                    <FaCheck /> Never
                  </span>
                </td>
              </tr>
              <tr>
                <th>
                  Cost to use <Cmd>!allstar</Cmd>
                </th>
                <td className="val">
                  <span className="yes">Free</span>
                </td>
                <td className="val paidcol">
                  <span className="yes">Free</span>
                </td>
              </tr>
              <tr>
                <th>Custom loadouts</th>
                <td>Allstar branded</td>
                <td className="val paidcol">Your own loadout</td>
              </tr>
              <tr>
                <th>Watermark applied</th>
                <td>Yes</td>
                <td className="val paidcol">Can be disabled with a loadout</td>
              </tr>
            </tbody>
          </Table>
        </TableScroll>
      </Section>

      <Section>
        <SecTitle>Questions &amp; Answers</SecTitle>
        {FAQ.map((item, i) => (
          <FaqItem key={item.q} open={open === i}>
            <button
              type="button"
              aria-expanded={open === i}
              onClick={() => setOpen(open === i ? -1 : i)}
            >
              {item.q}
              <span className="pm" aria-hidden="true">
                +
              </span>
            </button>
            {open === i && <p>{item.a}</p>}
          </FaqItem>
        ))}
      </Section>
    </Page>
  );
};

const Page = styled.div`
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 56px 22px 120px;
  color: ${(p) => p.theme.colors.ice};
`;

const Cmd = styled.span`
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  color: ${(p) => p.theme.colors.envy};
  font-weight: 600;
  white-space: nowrap;
`;

const Hero = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 44px;
  align-items: center;

  @media (max-width: ${(p) => p.theme.breakpoints[2]}) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const H1 = styled.h1`
  font-size: 58px;
  line-height: 1.04;
  font-weight: 800;
  letter-spacing: -0.025em;
  margin: 0;

  @media (max-width: ${(p) => p.theme.breakpoints[1]}) {
    font-size: 40px;
  }
`;

const Lede = styled.p`
  color: ${(p) => p.theme.colors.chalk};
  font-size: 19px;
  line-height: 1.55;
  max-width: 50ch;
  margin: 16px 0 0;
`;

const Strong = styled.span`
  color: ${(p) => p.theme.colors.ice};
  font-weight: 600;
`;

const BindHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin: 28px 0 10px;
`;

const BindLabel = styled.p`
  font-size: 15.5px;
  color: ${(p) => p.theme.colors.chalk};
  margin: 0;
`;

const BindHelp = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14.5px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.envy};
  cursor: help;

  svg {
    flex: none;
  }

  &:hover > [role="tooltip"],
  &:focus-within > [role="tooltip"] {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const BindTooltip = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 5;
  width: 320px;
  max-width: 86vw;
  padding: 14px 16px 14px 6px;
  background: ${(p) => p.theme.colors.ned};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.9);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease,
    visibility 0.15s;

  ol {
    margin: 0;
    padding-left: 24px;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  li {
    font-size: 14.5px;
    line-height: 1.5;
    font-weight: 400;
    color: ${(p) => p.theme.colors.ice};
  }
  &::after {
    content: "";
    position: absolute;
    top: -5px;
    right: 18px;
    width: 9px;
    height: 9px;
    background: ${(p) => p.theme.colors.ned};
    border-left: 1px solid rgba(255, 255, 255, 0.12);
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    transform: rotate(45deg);
  }
`;

const BindRow = styled.div`
  display: flex;
  gap: 10px;
  max-width: 480px;

  @media (max-width: 460px) {
    flex-direction: column;
  }
`;

const BindInput = styled.input`
  flex: 1;
  min-width: 0;
  min-height: 46px;
  padding: 0 14px;
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 15.5px;
  color: ${(p) => p.theme.colors.ice};
  background: ${(p) => p.theme.colors.navy500};
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.envy};
    outline-offset: 1px;
  }
`;

const CopyButton = styled.button`
  min-height: 46px;
  padding: 0 24px;
  font-weight: 700;
  font-size: 15px;
  white-space: nowrap;
  cursor: pointer;
  border: none;
  border-radius: 10px;
  background: ${(p) => (p.copied ? "transparent" : p.theme.colors.envy)};
  color: ${(p) => (p.copied ? p.theme.colors.envy : p.theme.colors.ice)};
  box-shadow: ${(p) =>
    p.copied ? `inset 0 0 0 1px ${p.theme.colors.envy}` : "none"};
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.copied ? "transparent" : "#5fe0b0")};
  }
  &:focus-visible {
    outline: 2px solid #5fe0b0;
    outline-offset: 2px;
  }
`;

const SubHint = styled.p`
  font-size: 14px;
  color: ${(p) => p.theme.colors.chalk};
  opacity: 0.8;
  margin: 11px 0 0;
`;

const MediaFrame = styled.div`
  position: relative;
  border-radius: 16px;
  box-shadow: 0 26px 70px -34px rgba(0, 0, 0, 0.85);
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(
    160deg,
    #1b2c33 0%,
    ${(p) => p.theme.colors.navy900} 75%
  );

  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fillBar = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const Processing = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  text-align: center;
  background: rgba(11, 17, 22, 0.82);
  backdrop-filter: blur(3px);
  animation: ${fadeIn} 0.2s ease;
`;

const Spinner = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: ${(p) => p.theme.colors.envy};
  animation: ${spin} 0.8s linear infinite;
`;

const ProcessingText = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.ice};

  small {
    display: block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.chalk};
    margin-bottom: 7px;
  }
  span {
    color: ${(p) => p.theme.colors.envy};
  }
`;

const ProcessingBar = styled.div`
  width: 70%;
  max-width: 240px;
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;

  &::after {
    content: "";
    display: block;
    height: 100%;
    border-radius: 999px;
    background: ${(p) => p.theme.colors.envy};
    animation: ${fillBar} 3.6s ease forwards;
  }
`;

const MediaCap = styled.p`
  text-align: center;
  font-size: 13.5px;
  color: ${(p) => p.theme.colors.chalk};
  opacity: 0.8;
  margin: 10px 0 0;
`;

const Section = styled.section`
  padding: 46px 0;
`;

const SecTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 26px;
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: ${(p) => p.theme.breakpoints[2]}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Step = styled.div`
  position: relative;
  background: ${(p) => p.theme.colors.navy500};
  border-radius: 14px;
  padding: 26px 24px 28px;

  /* line connecting the cards into a sequence (horizontal layout only) */
  &:not(:last-child)::after {
    content: "";
    position: absolute;
    top: 39px;
    right: -28px;
    width: 28px;
    height: 2px;
    background: rgba(255, 255, 255, 0.1);
  }
  @media (max-width: ${(p) => p.theme.breakpoints[2]}) {
    &:not(:last-child)::after {
      display: none;
    }
  }

  .head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
  }
  .ico {
    flex: none;
    display: grid;
    place-items: center;
    color: ${(p) => p.theme.colors.envy};
  }
  .ico svg {
    width: 26px;
    height: 26px;
  }
  .num {
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: ${(p) => p.theme.colors.chalk};
  }
  h3 {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
  }
  p {
    color: ${(p) => p.theme.colors.chalk};
    font-size: 15.5px;
    line-height: 1.6;
    margin: 9px 0 0;
  }
  p strong {
    color: ${(p) => p.theme.colors.ice};
    font-weight: 600;
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 540px;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 15px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    vertical-align: top;
    font-size: 15.5px;
  }
  thead th {
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: ${(p) => p.theme.colors.chalk};
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  }
  thead th.paid {
    color: ${(p) => p.theme.colors.envy};
  }
  tbody th {
    font-weight: 600;
    color: ${(p) => p.theme.colors.ice};
    width: 38%;
  }
  td {
    color: ${(p) => p.theme.colors.chalk};
  }
  td.val {
    color: ${(p) => p.theme.colors.ice};
  }
  .paidcol {
    background: ${(p) => p.theme.colors.envyAlpha10};
  }
  .yes {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: ${(p) => p.theme.colors.envy};
    font-weight: 700;
  }
  .yes svg {
    width: 14px;
    height: 14px;
  }
`;

const FaqItem = styled.div`
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  button {
    width: 100%;
    background: none;
    border: none;
    cursor: pointer;
    color: ${(p) => p.theme.colors.ice};
    font-family: inherit;
    font-weight: 600;
    font-size: 17.5px;
    text-align: left;
    padding: 18px 2px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
  }
  button .pm {
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 19px;
    color: ${(p) => p.theme.colors.envy};
    transform: ${(p) => (p.open ? "rotate(45deg)" : "none")};
    transition: transform 0.2s ease;
  }
  p {
    color: ${(p) => p.theme.colors.chalk};
    font-size: 15.5px;
    line-height: 1.6;
    max-width: 72ch;
    margin: 0;
    padding: 0 2px 18px;
  }
`;

export default AllstarPage;
